/*
 * Client-side validation over the data-val-* attributes the tag helpers emit.
 *
 * One engine: aspnet-client-validation, a dependency-free reader of the same
 * attributes jquery.validate.unobtrusive read. Phase 3.4 removed the second
 * one along with jQuery itself.
 *
 * It deliberately does NOT auto-scan the document. A form is validated because
 * something asked for it -- a handler calling attach() before it posts, or the
 * `data-mcp-validate` marker below -- so a partial that lands twice cannot end
 * up with two validators arguing over one submit.
 */
import { ValidationService } from 'aspnet-client-validation';
import { one, type Target } from './dom';

let service: ValidationService | null = null;

function native(): ValidationService {
    if (!service) {
        service = new ValidationService();
        // Same relaxation the legacy jQuery rule carries: a scheme and a non-empty host,
        // no opinion on hostname shape, so localhost / private-range config URLs pass.
        service.addProvider('url', value => !value || /^(?:https?|ftp):\/\/\S+$/i.test(value));
        // The stock rules (required, length, equalto, regex, range, ...). bootstrap()
        // is what normally registers them, but it also scans the whole document, which
        // this module refuses to do (see above) -- and without them every data-val rule
        // was "provider not implemented" and passed. First registration wins, so the
        // 'url' override above survives.
        (service as unknown as { addMvcProviders(): void }).addMvcProviders();
    }
    return service;
}

/** Attach validation to a form (or every form under a root) after it was inserted. */
export function attach(target: Target): void {
    const root = one(target);
    if (!root) return;
    const forms = root instanceof HTMLFormElement ? [root] : Array.from(root.querySelectorAll('form'));
    for (const form of forms) native().scan(form);
}

/**
 * Attach to any form that asked for it in markup with `data-mcp-validate`.
 *
 * This is what the anonymous account pages use -- register, invite acceptance,
 * the password-reset chain. They are ordinary full-page posts with no handler
 * of their own, and until Phase 3.4 they got their client validation from
 * jquery.validate.unobtrusive, which scanned the whole document on ready. The
 * marker replaces that blanket scan with an opt-in, so the console's own forms
 * (attached by the handler that posts them) can never be scanned twice.
 *
 * Registered as an enhancer, so a marked form works wherever it lands.
 */
export function enhanceValidatedForms(root: ParentNode = document): void {
    for (const form of root.querySelectorAll<HTMLFormElement>('form[data-mcp-validate]')) {
        if (form.dataset['mcpValidateAttached'] === 'true') continue;
        form.dataset['mcpValidateAttached'] = 'true';
        native().scan(form);
    }
}

/** Synchronous validity check, the `form.valid()` replacement. */
export function isValid(target: Target): boolean {
    const form = one(target);
    if (!(form instanceof HTMLFormElement)) return true;
    return native().isValid(form, true);
}

/** Full validation including async rules; resolves to the verdict. */
export function validate(target: Target): Promise<boolean> {
    const form = one(target);
    if (!(form instanceof HTMLFormElement)) return Promise.resolve(true);
    return native().validateForm(form);
}

/**
 * Show or clear one field's server-side message in its [data-valmsg-for]
 * span, with the same classes the unobtrusive engine toggles.
 */
export function setValidationError(id: string, message?: string | null): void {
    const span = document.querySelector<HTMLElement>(`span[data-valmsg-for="${CSS.escape(id)}"]`);
    const input = document.querySelector<HTMLElement>(`input[name="${CSS.escape(id)}"]`);
    const lines = (message ?? '').split(/<br\s*\/?>/i).filter(s => s.length > 0);
    if (span) {
        span.replaceChildren();
        lines.forEach((line, index) => {
            if (index > 0) span.appendChild(document.createElement('br'));
            span.appendChild(document.createTextNode(line));
        });
    }
    const invalid = lines.length > 0;
    span?.classList.toggle('field-validation-no-valid', invalid);
    span?.classList.toggle('field-validation-valid', !invalid);
    input?.classList.toggle('input-validation-error', invalid);
}

/**
 * Render a ModelState dictionary: keyed entries onto their field spans, the
 * '' entry into the container's .validation-summary (or the page's first).
 */
export function setValidationModelState(modelState: Record<string, string[] | string> | null | undefined, container?: string | null): void {
    if (!modelState) return;
    for (const [key, raw] of Object.entries(modelState)) {
        const messages = Array.isArray(raw) ? raw : [String(raw)];
        if (key) {
            setValidationError(key, messages.join('<br/>'));
            continue;
        }
        const scope = container ? one(container) : document;
        const summary = scope?.querySelector<HTMLElement>('div.validation-summary');
        if (!summary) continue;
        const list = document.createElement('ul');
        for (const message of messages) {
            const item = document.createElement('li');
            item.textContent = message;
            list.appendChild(item);
        }
        summary.replaceChildren(list);
        summary.classList.add('validation-summary-errors');
        summary.classList.remove('validation-summary-valid');
    }
}
